
import { Movie, PendingAction } from "@/types/movie";
import { ActionHandlers } from "./types";
import { createPendingAction, hasPendingExpired } from "./pendingActionUtils";
import { LIST_NAMES, SINGLE_LIST_TYPE } from "@/hooks/search/types/listConstants";
import { SingleListService } from "@/services/movie/singleListService";

/**
 * Toggles a movie's plan to watch status using single-list operations
 * FIXED: Unseen Gems watchlist toggles never show undo to prevent item removal
 */
export const toggleMoviePlanToWatch = async (
  movie: Movie,
  currentSection: string | undefined,
  pendingActions: PendingAction[],
  handlers: ActionHandlers
): Promise<void> => {
  if (!movie.tmdbId) {
    console.error("❌ Cannot toggle watchlist for movie without tmdbId:", movie);
    return;
  }

  console.log(`🎬 [WATCHLIST] toggleMoviePlanToWatch called:`, {
    title: movie.title,
    tmdbId: movie.tmdbId,
    currentPlanToWatch: movie.planToWatch,
    currentSection,
    willShowUndo: shouldShowUndo(movie, currentSection)
  });
  
  // Handle existing pending actions
  const existingAction = pendingActions.find(
    action => action.movieId === movie.tmdbId && action.type === 'planToWatch'
  );
  
  if (existingAction) {
    console.log(`🎬 [WATCHLIST] Found existing pending action - cancelling and applying immediately`);
    
    window.clearTimeout(existingAction.timeoutId);
    handlers.removePendingAction(existingAction.id);
    
    if (!hasPendingExpired(existingAction)) {
      const shouldBePlanToWatch = !existingAction.movieData.planToWatch;
      
      try {
        console.log(`🎬 [WATCHLIST] Executing immediate single-list operation for existing action cancel: ${movie.title} -> ${shouldBePlanToWatch}`);
        
        const result = shouldBePlanToWatch 
          ? await SingleListService.addToList(movie, SINGLE_LIST_TYPE.PLAN_TO_WATCH)
          : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.PLAN_TO_WATCH);
        
        if (!result.success) {
          console.error('❌ [WATCHLIST] Single-list operation failed (cancel action):', result.error);
          return;
        }
        
        console.log('✅ [WATCHLIST] Single-list operation success: watchlist status updated (cancel action)');
        
        const updatedMovie = {
          ...movie,
          planToWatch: shouldBePlanToWatch
        };
        
        handlers.updateMovieState(movie.tmdbId, updatedMovie);
        
      } catch (error) {
        console.error(`❌ [WATCHLIST] Error setting watchlist status for ${movie.title}:`, error);
        return;
      }
      return;
    }
  }
  
  // FIXED: Determine if undo should be shown - never for Unseen Gems watchlist toggles
  const shouldShowUndoAction = shouldShowUndo(movie, currentSection);
  
  if (shouldShowUndoAction) {
    console.log(`🎬 [WATCHLIST] Creating pending removal from watchlist for ${movie.title}`);
    const pendingAction = createPendingAction(
      movie, 
      'planToWatch', 
      currentSection || '',
      async (tmdbId, _, newPlanToWatchValue) => {
        console.log(`🎬 [WATCHLIST] Executing undo completion for ${movie.title}: ${newPlanToWatchValue}`);
        handlers.updateMovieState(tmdbId, { planToWatch: newPlanToWatchValue });
        console.log(`🎬 [WATCHLIST] Removal from watchlist completed for ${movie.title}`);
      },
      handlers.removePendingAction
    );
    
    handlers.addPendingAction(pendingAction);
  } else {
    // Apply immediately using single-list service
    try {
      const newPlanToWatchValue = !movie.planToWatch;
      console.log(`🎬 [WATCHLIST] Executing immediate toggle for ${movie.title}:`, {
        from: movie.planToWatch,
        to: newPlanToWatchValue,
        reason: 'immediate action or Unseen Gems context'
      });
      
      const result = newPlanToWatchValue 
        ? await SingleListService.addToList(movie, SINGLE_LIST_TYPE.PLAN_TO_WATCH)
        : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.PLAN_TO_WATCH);
      
      if (!result.success) {
        console.error('❌ [WATCHLIST] Single-list operation failed (immediate):', result.error);
        return;
      }
      
      console.log('✅ [WATCHLIST] Single-list operation success: watchlist status updated (immediate)', {
        tmdbId: movie.tmdbId,
        newPlanToWatchValue
      });
      
      const updatedMovie = {
        ...movie,
        planToWatch: newPlanToWatchValue
      };
      
      handlers.updateMovieState(movie.tmdbId, updatedMovie);
      
    } catch (error) {
      console.error(`❌ [WATCHLIST] Error toggling watchlist status for ${movie.title}:`, error);
    }
  }
};

/**
 * FIXED: Determine if undo should be shown - never for Unseen Gems to prevent removal
 */
function shouldShowUndo(movie: Movie, currentSection?: string): boolean {
  // Only show undo when removing from the watchlist tab itself
  return movie.planToWatch && currentSection === LIST_NAMES.WATCHLIST;
}
